import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress } from '@mui/material';
import { getSyncKnowledgeBaseStatus, queryLLM } from '../api-services/dm-projects.client';

interface GenerationModalProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    setTab: (tab: number) => void;
    setTempResponseLLM: (response: any) => void;
}

const GenerationModal: React.FC<GenerationModalProps> = ({ open, onClose, projectId, setTab, setTempResponseLLM }) => {
    const [error, setError] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);
    const [remainingTime, setRemainingTime] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const [isSynced, setIsSynced] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const key = `lastUploadTimestamp`;

    const hasToWait30Seconds = (projectId: string): boolean => {
        const raw = localStorage.getItem(key);
        
        if (!raw) {
          return false; 
        }
      
        const lastTs = parseInt(raw, 10);
        if (isNaN(lastTs)) {
          return false; 
        }
      
        const now = Date.now();
        const elapsed = now - lastTs; 
      
        return elapsed < 30_000;
    }

    const getRemainingWaitTime = (projectId: string): number => {
        const raw = localStorage.getItem(key);
        
        if (!raw) {
          return 0;
        }
      
        const lastTs = parseInt(raw, 10);
        if (isNaN(lastTs)) {
          return 0;
        }
      
        const now = Date.now();
        const elapsed = now - lastTs;
        const remaining = 30_000 - elapsed;
      
        return remaining > 0 ? Math.ceil(remaining / 1000) : 0;
    }

    const onGenerate = async () => {
        try {
            setIsGenerating(true);
            setError(null);
            const response = await queryLLM(projectId);
            console.log(response);
            setIsGenerating(false);
            setTempResponseLLM(response);
            setTab(1);
            onClose();
        } catch (error) {
            setError('Failed to generate content');
            setIsGenerating(false);
        }
    };

    const checkSyncStatus = async () => {
        setSyncing(true);
        setError(null);
        try {
            // Start polling for sync status
            intervalRef.current = setInterval(async () => {
                try {
                    const statusRes = await getSyncKnowledgeBaseStatus();
                    if (statusRes.overallStatus === 'HEALTHY') {
                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                            intervalRef.current = null;
                        }
                        setSyncing(false);
                        setIsSynced(true);
                    }
                } catch (err) {
                    setError('Failed to check sync status');
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    setSyncing(false);
                }
            }, 1000);
        } catch (err) {
            setError('Failed to check sync status');
            setSyncing(false);
        }
    };

    const startWaitTimer = () => {
        const remaining = getRemainingWaitTime(projectId);
        if (remaining <= 0) {
            setIsWaiting(false);
            setRemainingTime(0);
            // Check sync status immediately if no wait is needed
            checkSyncStatus();
            return;
        }

        setIsWaiting(true);
        setRemainingTime(remaining);

        intervalRef.current = setInterval(() => {
            const newRemaining = getRemainingWaitTime(projectId);
            setRemainingTime(newRemaining);
            
            if (newRemaining <= 0) {
                setIsWaiting(false);
                setRemainingTime(0);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                // Check sync status after wait period is over
                checkSyncStatus();
            }
        }, 1000);
    };

    useEffect(() => {
        if (open && projectId) {
            setError(null);
            setIsGenerating(false);
            setSyncing(false);
            setIsSynced(false);
            
            if (hasToWait30Seconds(projectId)) {
                startWaitTimer();
            } else {
                setIsWaiting(false);
                setRemainingTime(0);
                // Check sync status immediately if no wait is needed
                checkSyncStatus();
            }
        }
        
        return () => {
            // Clean up interval when component unmounts or modal closes
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [open, projectId]);

    // Determine modal title and content based on state
    let modalTitle = 'Ready to Generate';
    let modalText = 'You can now generate content for this project.';
    
    if (isWaiting) {
        modalTitle = 'Please Wait';
        modalText = `Please wait ${remainingTime} seconds before checking sync status.`;
    } else if (syncing) {
        modalTitle = 'Checking Sync Status...';
        modalText = 'Checking knowledge base sync status. Please wait.';
    } else if (isSynced) {
        modalTitle = 'Ready to Generate';
        modalText = 'Knowledge Base is synced! You can now generate content.';
    } else if (isGenerating) {
        modalTitle = 'Generating Content...';
        modalText = 'AI is generating content. Please wait.';
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
                {modalTitle}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 3, textAlign: 'center' }}>
                    {modalText}
                </Typography>
                {(isWaiting || syncing || isGenerating) && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <CircularProgress size={32} />
                    </Box>
                )}
                {error && (
                    <Typography color="error" sx={{ textAlign: 'center' }}>{error}</Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{
                        background: '#6435C9',
                        color: '#fff',
                        borderRadius: 2,
                        boxShadow: 'none',
                        '&:hover': { background: '#5127B0' },
                        '&:disabled': { background: '#9B7EDF' },
                    }}
                    onClick={onGenerate}
                    disabled={isWaiting || syncing || isGenerating || !isSynced}
                >
                    Generate Content
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                    sx={{
                        borderRadius: 2,
                        boxShadow: 'none',
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GenerationModal; 